import useUrlView from '../../ui/useUrlView';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { t } from '../../utils/i18n';
import { displayUserEmail, formatDateTime } from '../../utils/format';
import UserAvatar from '../../components/UserAvatar';
import UserSuspensionDialog from '../../components/UserSuspensionDialog/UserSuspensionDialog';
import { useUserSuspensionActions } from '../../utils/useUserSuspensionActions';
import UserDetailModal from '../../components/UserDetailModal/UserDetailModal';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { getUsers, getUsersSummary } from '../../api/authAdmin';
import PaginatedTableCard from '../../components/PaginatedTableCard/PaginatedTableCard';
import UserRowActionsMenu from '../../components/UserRowActionsMenu/UserRowActionsMenu';
import UserBanDialog from '../../components/UserBanDialog/UserBanDialog';
import UserDmRestrictionDialog from '../../components/UserDmRestrictionDialog/UserDmRestrictionDialog';
import UserChatRestrictionDialog from '../../components/UserChatRestrictionDialog/UserChatRestrictionDialog';
import { useUserBanActions } from '../../utils/useUserBanActions';
import { getUserAccountInactiveReason, isAdminBannedUser } from '../../utils/userBanActions';

const USER_TABLE_SKELETON_WIDTHS = [
  'w-10',
  'w-12',
  'w-24',
  'w-28',
  'w-32',
  'w-16',
  'w-20',
  'w-20',
  'w-20',
  'w-16',
  'w-16',
  'w-16',
  'w-24',
  'w-24',
  'w-16',
];

const PAGE_SIZE = 10;
/** Max rows fetched when server-side banned_only is unavailable (client fallback). */
const BANNED_ONLY_FETCH_SIZE = 100;

function getAccountStatusLabel(status) {
  return status === 1 ? t('user.accountStatus.active') : t('user.accountStatus.inactive');
}

function getLoginStatusLabel(status) {
  return status === 1 ? t('user.loginStatus.loggedIn') : t('user.loginStatus.loggedOut');
}

function getAccountTypeLabel(type) {
  switch (type) {
    case 'OFFICIAL':
      return t('user.accountType.official');
    case 'ADMIN':
      return t('user.accountType.admin');
    case 'BOT':
      return t('user.accountType.bot');
    case 'REVIEWER':
      return t('user.accountType.reviewer');
    case 'TESTER':
      return t('user.accountType.tester');
    case 'PARTNER_CELEB':
      return t('user.accountType.partnerCeleb');
    case 'USER':
      return t('user.accountType.user');
    default:
      return t('user.accountType.user');
  }
}

/** need_birthday / need_device_token / need_refresh_token: true = red pending, false = green DONE */
function MigrationChip({ needed }) {
  if (needed) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap border bg-red-500/15 text-red-400 border-red-500/30">
        {t('user.migration.pending')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap border bg-green-500/10 text-green-400 border-green-500/20">
      {t('user.migration.done')}
    </span>
  );
}

export default function UserPage() {
  const [columnView, setColumnView] = useUrlView('columns', ['basic', 'diagnostics'], 'basic');
  const diagnostics = columnView === 'diagnostics';
  const navigate = useNavigate();
  const location = useLocation();
  const { role, token, cacheSession, logout } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const isAdmin = role === 'admin';
  // These actions are shared by all signed-in internal developers. The API
  // revalidates the active Admin Web account for every request.
  const canManageRestrictions = Boolean(token) && ['admin', 'user'].includes(role);
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [excludeResigned, setExcludeResigned] = useState(false);
  const [bannedOnly, setBannedOnly] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userId = params.get('user_id');
    if (userId && /^[1-9]\d*$/.test(userId) && Number.isSafeInteger(Number(userId)) && params.get('environment') === apiEnvironment) setSelectedUserId(Number(userId));
    else setSelectedUserId(null);
  }, [location.search, apiEnvironment]);
  const [chatRestrictionUser, setChatRestrictionUser] = useState(null);
  const [dmRestrictionUser, setDmRestrictionUser] = useState(null);
  const [actionsMenuUserId, setActionsMenuUserId] = useState(null);
  useEffect(() => { setChatRestrictionUser(null); setDmRestrictionUser(null); setActionsMenuUserId(null); }, [apiEnvironment, role]);

  const usersQuery = useQuery({
    queryKey: ['users', apiEnvironment, page, PAGE_SIZE, searchQuery, excludeResigned, bannedOnly],
    queryFn: async () => {
      if (!bannedOnly) {
        return getUsers(page, PAGE_SIZE, token, searchQuery, { excludeResigned });
      }

      const serverPage = await getUsers(page, PAGE_SIZE, token, searchQuery, { bannedOnly: true });
      const serverItems = serverPage?.items ?? [];

      if (serverItems.length > 0 && serverItems.every(isAdminBannedUser)) {
        return serverPage;
      }

      // Server ignored banned_only or returned a non-ban page — fetch batch and filter client-side.
      const bulk = await getUsers(1, BANNED_ONLY_FETCH_SIZE, token, searchQuery, { bannedOnly: true });
      const banned = (bulk.items ?? []).filter(isAdminBannedUser);
      const start = (page - 1) * PAGE_SIZE;
      return {
        ...bulk,
        items: banned.slice(start, start + PAGE_SIZE),
        total_count: banned.length,
        page,
        page_size: PAGE_SIZE,
      };
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });

  const summaryQuery = useQuery({
    queryKey: ['usersSummary', apiEnvironment],
    queryFn: () => getUsersSummary(token),
    enabled: !!token,
  });

  useEffect(() => {
    const err = usersQuery.error || summaryQuery.error;
    if (err?.status === 401) {
      logout();
      navigate('/login', { replace: true });
    }
  }, [usersQuery.error, summaryQuery.error, logout, navigate]);

  const { items: paginatedUsers = [], total_count: totalCount = 0, page: currentPage = 1, page_size: pageSize = PAGE_SIZE } = usersQuery.data || {};
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const summary = useMemo(() => {
    const s = summaryQuery.data;
    if (!s) {
      return {
        totalUsers: null,
        activeUsers: null,
        inactiveUsers: null,
        loggedInUsers: null,
        loggedOutUsers: null,
        accountTypeCounts: { USER: 0, OFFICIAL: 0, ADMIN: 0, BOT: 0, REVIEWER: 0, TESTER: 0 },
      };
    }
    return {
      totalUsers: s.total_users ?? null,
      activeUsers: s.active_users ?? null,
      inactiveUsers: s.inactive_users ?? null,
      loggedInUsers: s.logged_in_users ?? null,
      loggedOutUsers: s.logged_out_users ?? null,
      accountTypeCounts: s.account_type_counts ?? { USER: 0, OFFICIAL: 0, ADMIN: 0, BOT: 0, REVIEWER: 0, TESTER: 0 },
    };
  }, [summaryQuery.data]);

  useEffect(() => {
    setActionsMenuUserId(null);
  }, [page, searchQuery, excludeResigned, bannedOnly]);

  const handleExcludeResignedChange = (e) => {
    const checked = e.target.checked;
    setExcludeResigned(checked);
    if (checked) setBannedOnly(false);
    setPage(1);
  };

  const handleBannedOnlyChange = (e) => {
    const checked = e.target.checked;
    setBannedOnly(checked);
    if (checked) {
      setExcludeResigned(false);
    }
    setPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchQuery(searchDraft.trim());
  };

  const handleSearchClear = () => {
    setSearchDraft('');
    setSearchQuery('');
    setPage(1);
  };
  const handleUserClick = (user) => {
    setSelectedUserId(user.user_id ?? user.UserID);
  };
  const closeModal = () => setSelectedUserId(null);
  const closeActionsMenu = () => setActionsMenuUserId(null);

  const handleModalUnauthorized = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const suspensionActions = useUserSuspensionActions({ token, apiEnvironment, role, cacheSession, onUnauthorized: handleModalUnauthorized });

  const { runBanAction, isPending: isBanPending, pendingUserId, dialog, confirmDialog, closeDialog } =
    useUserBanActions({
    token,
    apiEnvironment,
    role,
    cacheSession,
    onUnauthorized: handleModalUnauthorized,
  });

  const isSummaryLoading = summaryQuery.isPending || Boolean(summaryQuery.error);
  const isError = usersQuery.isError || summaryQuery.isError;
  const errorMessage = usersQuery.error?.message || summaryQuery.error?.message;

  return (
    <div className="min-h-screen py-8 px-3 sm:px-5 lg:px-6 bg-dark-bg">
      <div className="w-full max-w-[min(100%,1920px)] mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-dark-text flex items-center gap-2">
              {t('user.title')}
              {isAdmin && (
                <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-primary/20 text-primary border border-primary/40">
                  Admin
                </span>
              )}
            </h1>
            <p className="text-dark-text-secondary text-sm mt-1">
              {t('user.description')}
            </p>
          </div>
        </div>

        {isError && (
          <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {errorMessage}
          </div>
        )}

        <details className="mb-5 rounded-lg border border-dark-border bg-dark-card p-3"><summary className="text-sm text-dark-text-secondary">{t('adminUi.accountStatistics')}</summary>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <div className="bg-dark-card border border-dark-border rounded-lg p-4">
            <p className="text-xs text-dark-text-secondary mb-1">
              {t('user.summary.totalUsers')}
            </p>
            <p className="text-2xl font-semibold text-dark-text">
              {isSummaryLoading ? '-' : (summary.totalUsers ?? '—')}
            </p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-lg p-4">
            <p className="text-xs text-dark-text-secondary mb-1">
              {t('user.summary.accountStatus')}
            </p>
            <div className="flex items-baseline gap-4">
              <span className="text-sm text-primary">
                {t('user.summary.active')}: {isSummaryLoading ? '-' : (summary.activeUsers ?? '—')}
              </span>
              <span className="text-sm text-red-400">
                {t('user.summary.inactive')}: {isSummaryLoading ? '-' : (summary.inactiveUsers ?? '—')}
              </span>
            </div>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-lg p-4">
            <p className="text-xs text-dark-text-secondary mb-1">
              {t('user.summary.loginStatus')}
            </p>
            <div className="flex items-baseline gap-4">
              <span className="text-sm text-primary">
                {t('user.summary.loggedIn')}: {isSummaryLoading ? '-' : (summary.loggedInUsers ?? '—')}
              </span>
              <span className="text-sm text-dark-text-secondary">
                {t('user.summary.loggedOut')}: {isSummaryLoading ? '-' : (summary.loggedOutUsers ?? '—')}
              </span>
            </div>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-lg p-4">
            <p className="text-xs text-dark-text-secondary mb-1">
              {t('user.summary.accountType')}
            </p>
            <div className="flex flex-wrap gap-2 mt-1 text-xs text-dark-text-secondary">
              <span>{t('user.accountType.user')}: {isSummaryLoading ? '-' : (summary.accountTypeCounts.USER ?? 0)}</span>
              <span>{t('user.accountType.official')}: {isSummaryLoading ? '-' : (summary.accountTypeCounts.OFFICIAL ?? 0)}</span>
              <span>{t('user.accountType.admin')}: {isSummaryLoading ? '-' : (summary.accountTypeCounts.ADMIN ?? 0)}</span>
              <span>{t('user.accountType.bot')}: {isSummaryLoading ? '-' : (summary.accountTypeCounts.BOT ?? 0)}</span>
              <span>{t('user.accountType.reviewer')}: {isSummaryLoading ? '-' : (summary.accountTypeCounts.REVIEWER ?? 0)}</span>
              <span>{t('user.accountType.tester')}: {isSummaryLoading ? '-' : (summary.accountTypeCounts.TESTER ?? 0)}</span>
            </div>
          </div>
        </div>

        </details>

        <form
          onSubmit={handleSearchSubmit}
          className="mb-4 rounded-xl border border-dark-border bg-gradient-to-br from-dark-card to-dark-surface/40 p-4 shadow-sm"
        >
          <label htmlFor="user-search" className="block text-xs font-medium tracking-wide text-dark-text-secondary uppercase">
            {t('user.search.label')}
          </label>
          <div className="mt-2 w-full max-w-lg">
            <div className="flex min-h-[2.75rem] w-full min-w-0 overflow-hidden rounded-lg border border-dark-border bg-dark-bg shadow-inner transition-colors focus-within:border-primary/60 focus-within:ring-1 focus-within:ring-primary/35">
              <input
                id="user-search"
                type="search"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                placeholder={t('user.search.placeholder')}
                className="min-w-0 flex-1 border-0 bg-transparent py-2.5 pl-4 pr-2 text-sm text-dark-text placeholder:text-dark-text-secondary/60 outline-none focus:ring-0"
                autoComplete="off"
              />
              <div className="flex shrink-0 divide-x divide-dark-border/80">
                <button
                  type="submit"
                  aria-label={t('user.search.submit')}
                  title={t('user.search.submit')}
                  className="flex items-center justify-center bg-primary/15 px-3.5 text-lg leading-none text-primary transition-colors hover:bg-primary/25 active:bg-primary/35"
                >
                  🔍
                </button>
                <button
                  type="button"
                  onClick={handleSearchClear}
                  aria-label={t('user.search.clear')}
                  title={t('user.search.clear')}
                  className="flex items-center justify-center bg-dark-surface/50 px-3.5 text-lg leading-none text-dark-text-secondary transition-colors hover:bg-dark-surface hover:text-primary"
                >
                  🗑️
                </button>
              </div>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-dark-text-secondary/90">{t('user.search.hint')}</p>
          </div>
        </form>

        <div className="mb-4 rounded-xl border border-dark-border bg-dark-card p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <label className="text-xs text-dark-text-secondary flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={excludeResigned}
              disabled={bannedOnly}
              onChange={handleExcludeResignedChange}
              className="accent-primary disabled:opacity-40"
            />
            <span>{t('user.filter.excludeResigned')}</span>
          </label>
          <label className="text-xs text-dark-text-secondary flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={bannedOnly}
              disabled={excludeResigned}
              onChange={handleBannedOnlyChange}
              className="accent-primary disabled:opacity-40"
            />
            <span>{t('user.filter.bannedOnly')}</span>
          </label>
        </div>

        <label className="mb-4 inline-flex items-center gap-2 text-sm text-dark-text-secondary"><input type="checkbox" checked={diagnostics} onChange={event => setColumnView(event.target.checked ? 'diagnostics' : 'basic')} />{t('adminUi.diagnosticView')}</label>

        <PaginatedTableCard
          error={usersQuery.error}
          dataUpdatedAt={usersQuery.dataUpdatedAt}
          isPending={usersQuery.isPending}
          isFetching={usersQuery.isFetching}
          isEmpty={!usersQuery.isPending && paginatedUsers.length === 0}
          skeletonRows={PAGE_SIZE}
          skeletonCellWidths={diagnostics ? USER_TABLE_SKELETON_WIDTHS : USER_TABLE_SKELETON_WIDTHS.slice(0, 8)}
          colSpan={diagnostics ? 15 : 8}
          minHeight="min-h-[32rem]"
          tableClassName={diagnostics ? "w-full min-w-[1400px]" : "w-full min-w-[820px]"}
          loadingLabel={t('user.table.loading') || t('common.loading')}
          emptyLabel={
            bannedOnly
                ? t('user.table.emptyBannedOnly')
                : t('user.table.empty')
          }
          page={page}
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setPage}
          prevLabel={t('user.pagination.prev')}
          nextLabel={t('user.pagination.next')}
          summary={t('user.pagination.page', { page: currentPage, totalPages })}
          header={(
            <tr className="border-b border-dark-border">
              <th className="text-left py-3 px-2 text-dark-text-secondary font-medium text-sm w-14 min-w-14">{t('user.table.avatar')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.userId')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.username')}</th>
              {diagnostics && <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.name')}</th>}
              {diagnostics && <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.email')}</th>}
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm whitespace-nowrap">{t('user.table.point')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.accountStatus')}</th>
              {diagnostics && <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.loginStatus')}</th>}
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.accountType')}</th>
              {diagnostics && <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm whitespace-nowrap">🎂 {t('user.table.needBirthday')}</th>}
              {diagnostics && <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm whitespace-nowrap">🔑 {t('user.table.needDeviceToken')}</th>}
              {diagnostics && <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm whitespace-nowrap">🔐 {t('user.table.needRefreshToken')}</th>}
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.lastLogin')}</th>
              {diagnostics && <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('user.table.createdAt')}</th>}
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm whitespace-nowrap">{t('user.table.actions')}</th>
            </tr>
          )}
        >
          {paginatedUsers.map((user) => (
            <tr
              key={user.user_id ?? user.UserID}
              className="border-b border-dark-border last:border-0 hover:bg-dark-surface/50"
            >
              <td className="py-3 px-2 align-middle w-14 min-w-14">
                <UserAvatar user={user} size={32} />
              </td>
              <td
                className="py-3 px-4 text-primary cursor-pointer underline-offset-2 hover:underline"
                onClick={() => handleUserClick(user)}
                role="button"
              >
                {user.user_id ?? user.UserID ?? '-'}
              </td>
              <td className="py-3 px-4 text-dark-text">{user.username ?? user.Username ?? '-'}</td>
              {diagnostics && <td className="py-3 px-4 text-dark-text">{user.name ?? user.Name ?? '-'}</td>}
              {diagnostics && <td className="py-3 px-4 text-dark-text">{displayUserEmail(user)}</td>}
              <td className="py-3 px-4 text-dark-text tabular-nums whitespace-nowrap">
                {user.point_balance_status === 'unavailable' ? '조회 불가' : Number(user.point_balance ?? user.PointBalance ?? 0).toLocaleString()}
              </td>
              <td className="py-3 px-4">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    (user.account_status ?? user.AccountStatus) === 1 ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                  }`}
                >
                  {getAccountStatusLabel(user.account_status ?? user.AccountStatus ?? 0)}
                </span>{(user.account_status ?? user.AccountStatus) === 0 && [1,2,3,4].includes(getUserAccountInactiveReason(user)) && <small className="mt-1 block text-xs text-dark-text-secondary">{t(`user.inactiveReason.${['','deactivation','resignation','ban','suspension'][getUserAccountInactiveReason(user)]}`)}</small>}
              </td>
              {diagnostics && <td className="py-3 px-4">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    (user.login_status ?? user.LoginStatus) === 1 ? 'bg-blue-500/10 text-blue-400' : 'bg-dark-surface text-dark-text-secondary'
                  }`}
                >
                  {getLoginStatusLabel(user.login_status ?? user.LoginStatus ?? 0)}
                </span>
              </td>}
              <td className="py-3 px-4 text-dark-text-secondary text-sm">
                {getAccountTypeLabel(user.account_type ?? user.AccountType ?? 'USER')}
              </td>
              {diagnostics && <td className="py-3 px-4">
                <MigrationChip needed={user.need_birthday ?? user.NeedBirthday ?? false} />
              </td>}
              {diagnostics && <td className="py-3 px-4">
                <MigrationChip needed={user.need_device_token ?? user.NeedDeviceToken ?? false} />
              </td>}
              {diagnostics && <td className="py-3 px-4">
                <MigrationChip needed={user.need_refresh_token ?? user.NeedRefreshToken ?? false} />
              </td>}
              <td className="py-3 px-4 text-dark-text-secondary text-sm">
                {formatDateTime(user.login_at ?? user.LoginAt)}
              </td>
              {diagnostics && <td className="py-3 px-4 text-dark-text-secondary text-sm">
                {formatDateTime(user.created_at ?? user.CreatedAt)}
              </td>}
              <td className="py-3 px-4 align-middle whitespace-nowrap overflow-visible">
                <UserRowActionsMenu
                  user={user}
                  isOpen={actionsMenuUserId === (user.user_id ?? user.UserID)}
                  onToggle={() => {
                    const id = user.user_id ?? user.UserID;
                    setActionsMenuUserId((prev) => (prev === id ? null : id));
                  }}
                  onClose={closeActionsMenu}
                  onViewDetail={() => handleUserClick(user)}
                  onSuspensionAction={canManageRestrictions ? suspensionActions.open : undefined}
                  apiEnvironment={apiEnvironment}
                  onChatRestriction={canManageRestrictions ? (user) => setChatRestrictionUser({ user, apiEnvironment }) : undefined}
                  onDmRestriction={isAdmin && token ? (target) => setDmRestrictionUser({ user: target, apiEnvironment }) : undefined}
                  onBanAction={runBanAction}
                  isBanPending={isBanPending}
                  pendingUserId={pendingUserId}
                />
              </td>
            </tr>
          ))}
        </PaginatedTableCard>

        {selectedUserId && token && (
          <UserDetailModal
            key={`${apiEnvironment}:${selectedUserId}`}
            userId={selectedUserId}
            token={token}
            onClose={closeModal}
            onUnauthorized={handleModalUnauthorized}
            onNavigateUserId={setSelectedUserId}
            initialUser={paginatedUsers.find((u) => (u.user_id ?? u.UserID) === selectedUserId)}
            runSuspensionAction={canManageRestrictions ? suspensionActions.open : undefined}
            runBanAction={runBanAction}
            isBanPending={isBanPending}
            pendingUserId={pendingUserId}
          />
        )}

        {canManageRestrictions && <UserSuspensionDialog actions={suspensionActions} />}
        {dmRestrictionUser?.apiEnvironment === apiEnvironment && isAdmin && token && <UserDmRestrictionDialog user={dmRestrictionUser.user} token={token} apiEnvironment={apiEnvironment} onClose={() => setDmRestrictionUser(null)} onUnauthorized={logout} />}
        {chatRestrictionUser?.apiEnvironment === apiEnvironment && canManageRestrictions && <UserChatRestrictionDialog key={`${apiEnvironment}:${chatRestrictionUser.user.user_id ?? chatRestrictionUser.user.UserID}`} user={chatRestrictionUser.user} token={token} apiEnvironment={apiEnvironment} onClose={() => setChatRestrictionUser(null)} />}

        <UserBanDialog
          dialog={dialog}
          onConfirm={confirmDialog}
          onCancel={closeDialog}
          loading={isBanPending}
        />


      </div>
    </div>
  );
}
